import { FC } from "react";
import Icon from "@mdi/react";
import { ShareOptions } from "@capacitor/share";
import {
  mdiArchiveOutline,
  mdiCancel,
  mdiContentCopy,
  mdiDotsHorizontal,
  mdiDotsVertical,
  mdiMotionPlayOutline,
  mdiRestart,
  mdiShare,
  mdiTrashCanOutline,
} from "@mdi/js";

import SpeedDial from "components/SpeedDial";
import useShare from "hooks/useShare";
import { EventRow } from "api/event";
import { getHost } from "utils/device";
import useCopyToClipboard from "hooks/useCopyToClipboard";
import useNotifier from "hooks/useNotifier";

export interface EventSpeedDialProps {
  activeCountdown: boolean;
  event: EventRow;
  isAdmin: boolean;
  groupId: number | null;
  handleArchiveEvent: () => Promise<void>;
  handleResetVotes: () => Promise<void>;
  handleDeleteEvent: () => Promise<void>;
  handleResumeEvent: () => void;
  handleStopCountdown: () => void;
}

const EventSpeedDial: FC<EventSpeedDialProps> = (props: EventSpeedDialProps) => {
  const {
    activeCountdown,
    event,
    groupId,
    handleArchiveEvent,
    handleDeleteEvent,
    handleResetVotes,
    handleResumeEvent,
    handleStopCountdown,
    isAdmin,
  } = props;
  const { canShare, share } = useShare();
  const { canCopy, copy } = useCopyToClipboard();
  const { showNotification } = useNotifier();
  const shareEventInfo: ShareOptions = {
    title: `${event.name}`,
    text: `${event.name}\n`,
    url: `${getHost()}/group/${groupId}/event/${event.id}`,
  };

  function handleCopy() {
    if (canCopy) {
      copy(shareEventInfo.url ?? "Unable to copy event link");
      showNotification({ message: "Link copied", severity: "success" });
    }
  }

  /**
   * Returns the SpeedDial which has actions which can be made when the event
   * is still active
   *
   * @returns
   */
  function renderActiveEventSpeedDial(): JSX.Element | null {
    const actions = [];

    if (canShare) {
      actions.push({
        icon: <Icon path={mdiShare} size={1} />,
        name: "Share",
        action: () => share(shareEventInfo),
      });
    } else if (canCopy) {
      actions.push({
        icon: <Icon path={mdiContentCopy} size={1} />,
        name: "Copy event link",
        action: handleCopy,
      });
    }

    if (isAdmin) {
      actions.push(
        {
          icon: <Icon path={mdiRestart} size={1} />,
          name: "Reset votes",
          action: handleResetVotes,
        },

        {
          icon: <Icon path={mdiCancel} size={1} />,
          name: "Stop countdown",
          action: handleStopCountdown,
        }
      );
    }

    if (actions.length === 0) return null;

    return (
      <SpeedDial
        actions={actions}
        ariaLabel='More options'
        icon={<Icon path={mdiDotsHorizontal} size={1} />}
        openIcon={<Icon path={mdiDotsVertical} size={1} />}
      />
    );
  }

  /**
   * Returns the SpeedDial which has actions which can be made when the event
   * is still active
   *
   * @returns
   */
  function renderInactiveEventSpeedDial(): JSX.Element | null {
    const actions = [];

    if (canShare) {
      actions.push({
        icon: <Icon path={mdiShare} size={1} />,
        name: "Share",
        action: () => share(shareEventInfo),
      });
    } else if (canCopy) {
      actions.push({
        icon: <Icon path={mdiContentCopy} size={1} />,
        name: "Copy event link",
        action: handleCopy,
      });
    }

    if (isAdmin) {
      actions.push(
        {
          icon: <Icon path={mdiArchiveOutline} size={1} />,
          name: "Archive",
          action: handleArchiveEvent,
        },
        {
          icon: <Icon path={mdiTrashCanOutline} size={1} />,
          name: "Delete",
          action: handleDeleteEvent,
        },
        {
          icon: <Icon path={mdiMotionPlayOutline} size={1} />,
          name: "Resume",
          action: handleResumeEvent,
        }
      );
    }

    if (actions.length === 0) return null;

    return (
      <SpeedDial
        actions={actions}
        ariaLabel='More options'
        icon={<Icon path={mdiDotsHorizontal} size={1} />}
        openIcon={<Icon path={mdiDotsVertical} size={1} />}
      />
    );
  }

  return activeCountdown ? renderActiveEventSpeedDial() : renderInactiveEventSpeedDial();
};

export default EventSpeedDial;
